import { Link } from 'react-router-dom';
import { ArrowRight, Plus, ScrollText, StickyNote } from 'lucide-react';
import { PageBody } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { StatusDot } from '@/components/ui/StatusDot';
import { useTenant } from './context';
import { relativeTime } from '@/lib/format';
import { REGION_LABEL } from '@/lib/tier';

export function TenantOverview() {
  const { tenant, workspaces, audit } = useTenant();
  const live = workspaces.filter(w => w.status === 'live').length;
  const degraded = workspaces.filter(w => w.status === 'degraded' || w.status === 'error').length;

  // NousViz versions running across this customer's workspaces.
  const versions = Array.from(new Set(workspaces.map(w => w.version).filter(Boolean)));
  const latest = workspaces.find(w => w.latestVersion)?.latestVersion ?? '';
  const hasDrift = versions.some(v => v && latest && v !== latest);
  const versionsLabel = versions.length === 0 ? '—' : versions.length === 1 ? versions[0] : `${versions.length} versions`;
  const versionsHint = versions.length === 0
    ? 'No probe data'
    : hasDrift
      ? `Drift · latest is ${latest}`
      : 'All on latest';

  return (
    <PageBody>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
        <Kpi label="Instances" value={String(workspaces.length)} hint={`${live} live · ${degraded} unhealthy`} />
        <Kpi label="Region" value={REGION_LABEL[tenant.region]} />
        <Kpi label="Support owner" value={tenant.supportOwner ?? '—'} />
        <Kpi
          label="NousViz version"
          value={versionsLabel}
          hint={versionsHint}
          tone={hasDrift ? 'warn' : undefined}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 space-y-4">
          <Card>
            <CardHeader
              title="Instances"
              description="All NousViz instances for this customer."
              actions={
                <Link to={`/tenants/${tenant.slug}/workspaces`} className="text-sm text-accent hover:underline inline-flex items-center gap-1">
                  Detail <ArrowRight size={12} />
                </Link>
              }
            />
            {workspaces.length > 0 ? (
              <Table>
                <THead>
                  <tr>
                    <TH>Instance</TH>
                    <TH>Region</TH>
                    <TH>Version</TH>
                    <TH>Last health check</TH>
                  </tr>
                </THead>
                <TBody>
                  {workspaces.map(w => (
                    <TR key={w.id}>
                      <TD>
                        <Link to={`/tenants/${tenant.slug}/workspaces/${w.id}`} className="flex items-center gap-2.5 hover:text-accent">
                          <StatusDot status={w.status} />
                          <div>
                            <div className="font-medium text-fg">{w.name}</div>
                            <div className="text-2xs text-fg-subtle font-mono">{w.id}</div>
                          </div>
                        </Link>
                      </TD>
                      <TD className="text-fg-muted">{REGION_LABEL[w.region]}</TD>
                      <TD>
                        <span className="font-mono text-xs text-fg-muted">{w.version || '—'}</span>
                        {w.version && w.latestVersion && w.version !== w.latestVersion && (
                          <Badge tone="warn" className="ml-1.5">Drift</Badge>
                        )}
                      </TD>
                      <TD className="text-fg-muted">{relativeTime(w.lastActiveAt)}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            ) : (
              <div className="px-5 py-6 text-center text-sm text-fg-muted">No instances yet.</div>
            )}
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader title="Quick actions" />
            <CardBody className="space-y-2">
              {/* Per-workspace actions (restart, snapshot, resize, destroy)
                  live on the workspace detail page where their target is
                  unambiguous — no fleet-wide buttons here until the backend
                  has them for real. */}
              <Link to={`/tenants/${tenant.slug}/workspaces`} className="block">
                <Button variant="secondary" size="sm" className="w-full justify-start" iconLeft={<Plus size={14} />}>
                  Create instance
                </Button>
              </Link>
              <Link to={`/tenants/${tenant.slug}/logs`} className="block">
                <Button variant="secondary" size="sm" className="w-full justify-start" iconLeft={<ScrollText size={14} />}>
                  Search this customer's logs
                </Button>
              </Link>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Recent activity"
              description="Latest operator + system events."
              actions={<Link to={`/tenants/${tenant.slug}/events`} className="text-sm text-accent">All →</Link>}
            />
            <ul className="px-5 py-3 space-y-2.5 text-xs">
              {audit.slice(0, 6).map(a => (
                <li key={a.id} className="flex items-start gap-2">
                  <span className="text-fg-subtle mt-0.5">·</span>
                  <div className="min-w-0">
                    <div className="text-fg">{a.staffName}</div>
                    <div className="text-fg-muted truncate">
                      <span className="font-mono">{a.action}</span> · {relativeTime(a.timestamp)}
                    </div>
                  </div>
                </li>
              ))}
              {audit.length === 0 && (
                <li className="text-fg-subtle text-center py-2">No activity yet.</li>
              )}
            </ul>
          </Card>

          <Card>
            <CardHeader title="Owner" />
            <CardBody className="text-sm">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-surface-2 border border-border flex items-center justify-center font-semibold text-fg">
                  {tenant.ownerName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div>
                  <div className="font-medium text-fg">{tenant.ownerName}</div>
                  <a className="text-2xs text-fg-subtle hover:text-accent" href={`mailto:${tenant.ownerEmail}`}>
                    {tenant.ownerEmail}
                  </a>
                </div>
              </div>
              {tenant.supportOwner && (
                <div className="mt-3 text-xs text-fg-muted">
                  Support owner: <span className="text-fg font-medium">{tenant.supportOwner}</span>
                </div>
              )}
            </CardBody>
          </Card>

          {tenant.notes && (
            <Card>
              <CardHeader title={<><StickyNote size={12} className="inline -mt-0.5 mr-1.5 text-fg-muted" /> Notes</>} />
              <CardBody className="text-xs text-fg-muted leading-relaxed">
                {tenant.notes}
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </PageBody>
  );
}

function Kpi({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'warn' | 'danger';
}) {
  return (
    <Card className="px-4 py-3.5">
      <div className="text-2xs uppercase tracking-wider text-fg-subtle font-semibold">{label}</div>
      <div className="text-xl font-semibold tabnum mt-1">{value}</div>
      {hint && (
        <div className={
          tone === 'warn' ? 'text-xs mt-0.5 text-[color:var(--warn)]' :
          tone === 'danger' ? 'text-xs mt-0.5 text-[color:var(--danger)]' :
          'text-xs mt-0.5 text-fg-muted'
        }>
          {hint}
        </div>
      )}
    </Card>
  );
}
